import type { FacetType, FacetValues } from '@uncinc/react-searchkit/types';

import type { SearchAllContentFacet } from '../components/SearchAllContent/types';

/**
 * Converts an array of Facet objects into a UI state object
 * suitable for initializing facet values in searchkit.
 * Each facet is grouped by its type and mapped to its attribute and default value.
 *
 * ### Example usage:
 * ```tsx
 *   getFacetUiState([
 *     { type: 'refinementList', attribute: 'function', defaultValue: 'Senior Scientist' },
 *     { type: 'refinementList', attribute: 'department', defaultValue: 'Biology' },
 *     { type: 'menu', attribute: 'location', defaultValue: 'Texel' }
 *   ]);
 *   // returns:
 *   // {
 *   //   refinementList: { function: ['Senior Scientist'], department: ['Biology'] },
 *   //   menu: { location: 'Texel' }
 *   // }
 * ```
 *
 * @param facets - Array of facet definitions with type, attribute, and defaultValue.
 * @returns An object mapping facet types to their attribute/value pairs.
 */
export default function getInitialFacetUiState(facets: SearchAllContentFacet[]): Pick<FacetValues, FacetType> {
  return facets.reduce<Pick<FacetValues, FacetType>>((state, { type = 'refinementList', attribute, defaultValue }) => {
    if (defaultValue === undefined) {
      return state;
    }

    return {
      ...state,
      [type]: {
        ...(state[type] ?? {}),
        // RefinementList and hierarchicalMenu types expect an array defaultValue,
        // so in those cases we put defaultValue in an array if it is not already so.
        [attribute]: (type === 'refinementList' || type === 'hierarchicalMenu') && !Array.isArray(defaultValue)
          ? [defaultValue]
          : defaultValue,
      },
    };
  }, {});
}
